import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote, Users } from 'lucide-react';

export const Testimonials = () => {
  const testimonialsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('fade-in');
            }, index * 150);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = testimonialsRef.current?.querySelectorAll('.testimonial-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      name: 'Kalpak Mashalkar',
      role: 'Patient',
      image: Users,
      text: 'I had a very good experience at Nanded Hospital in Pune.Entire staff start from housekeeping to doctors are professional, caring ,attentive, and efficient. The facility is clean and well-maintained, creating a comfortable environment. Right from booking appointments to the final discharge, it was highly organized in a professional way. The Sisters/ Staff/ Doctors were extremely courteous and helpful. Highly recommend!...',
      rating: 5,
    },
    {
      name: 'Eknath Jadhav',
      role: 'Patient',
      image: Users,
      text: 'I would like to express my heartfelt gratitude for the care and treatment I received at Nanded Hospital. The doctors and nursing staff were extremely cooperative, ensuring a clean environment and prompt medical attention. A special thanks to Dr. Sunil More sir, MBBS, for his polite demeanor and extensive knowledge, which made a significant difference in my recovery. Thank you all for your dedicated service.',
      rating: 5,
    },
    {
      name: 'Shilpa K',
      role: 'Patient',
      image: 'Users',
      text: 'I Visited Dr. Garima Singh, ENT specialist here. She took the time to really listen to my concerns and explained everything in a clear, patient, and compassionate manner. Her knowledge and expertise were immediately evident, and I felt completely confident in the care I was receiving. If you’re looking for a skilled and caring ENT specialist who genuinely puts patients first, look no further than Dr. Garima Singh. I’m so grateful to have found such a dedicated professional!',
      rating: 5,
    },
  ];

  return (
    <section className="py-24 bg-[hsl(var(--background))]" ref={testimonialsRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
            <Star className="w-5 h-5 text-[hsl(var(--primary))]" />
            <span className="text-sm font-semibold text-[hsl(var(--primary))]">
              Patient Reviews
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
            What Our Patients
            <span className="block mt-2 text-gradient">Say About Us</span>
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            Real experiences from real patients who trusted us with their health.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="testimonial-card opacity-0"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Card className="h-full hover:shadow-[var(--shadow-xl)] transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-[hsl(var(--primary))]/20">
                <CardContent className="pt-8 space-y-6">
                  {/* Quote Icon */}
                  <div className="flex justify-between items-start">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                      <Quote className="w-6 h-6 text-[hsl(var(--primary))]" />
                    </div>
                    {/* Star Rating */}
                    <div className="flex space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" 
                        />
                      ))}
                    </div>
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-[hsl(var(--muted-foreground))] leading-relaxed italic">
                    "{testimonial.text}"
                  </p>

                  {/* Author Info */}
                  <div className="flex items-center space-x-4 pt-4 border-t border-[hsl(var(--border))]">
                    <div>
                      <Users
                        src={testimonial.image}
                        className="w-14 h-14 rounded-full object-cover shadow-[var(--shadow-sm)]"
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-[hsl(var(--foreground))]">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          <div className="text-center">
            <p className="text-4xl font-bold text-[hsl(var(--primary))]">4.9/5</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Average Rating</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-[hsl(var(--primary))]">50K+</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Happy Patients</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-[hsl(var(--primary))]">10K+</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">5-Star Reviews</p>
          </div>
          <div className="text-center">
            <p className="text-4xl font-bold text-[hsl(var(--primary))]">98%</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Satisfaction Rate</p>
          </div>
        </div>
      </div>
    </section>
  );
};